"use client";

import { cn } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Image from '@/components/ui/Image';
import { useEffect, useState } from "react";
import parse from "html-react-parser";
import Link from "next/link";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { SectionWrapper } from "../components/SectionWraper";

interface GalleryItem {
  image?: string;
  imageAltText?: string;
  title?: string;
  content?: string;
}

interface Gallery1Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  galleryItems?: GalleryItem[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Gallery1 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Gallery 1",
  themeChangeOnScroll = false,
  title,
  content,  
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  galleryItems,
  attributes,
}: Gallery1Props) => {  
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  useEffect(() => {
    if (!carouselApi) {
      return;
    }
    const updateSelection = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    };
    updateSelection();
    carouselApi.on("select", updateSelection);
    return () => {
      carouselApi.off("select", updateSelection);
    };
  }, [carouselApi]);

  return (
    <SectionWrapper 
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="gallery1"
      attributes={attributes}
    > 
      <div className="container">
        <div className="mb-6 lg:mb-16 gap-0 lg:gap-20 flex flex-col justify-between sm:mb-14 sm:flex-row sm:items-center">
          <div>
            {title && (
              <h2 className="sm:max-w-[500px] lg:max-w-[800px] text-center sm:text-left">
                {parse(title)}
              </h2>
            )}
            {content && content.trim() && (
              <div className="mt-6 paragraph-lg text-muted-foreground text-center sm:text-left">
                {parse(content)}
              </div>
            )}
            {hasButton && (
              <>
                <Button asChild variant={buttonStyle} size="lg" className="mt-2 w-full sm:w-auto">
                  <Link href={buttonLink ?? ""}>
                    {buttonText}
                  </Link>
                </Button>
                {microCopy && (
                  <div className="mt-2 paragraph-sm text-muted-foreground text-center sm:text-left">
                    {microCopy}
                  </div>
                )}
              </>
            )}
            
          </div>
          <div className="mt-6 shrink-0 items-center justify-end gap-2 sm:mt-0 hidden sm:flex">
            <Button
              size="iconSm"
              variant="outline"
              grayBorder
              onClick={() => {
                carouselApi?.scrollPrev();
              }}
              disabled={!canScrollPrev}
              className="disabled:pointer-events-auto"
            >
              <span className="material-symbols-outlined">
                arrow_back  
              </span>
            </Button>
            <Button
              size="iconSm"
              variant="outline"
              grayBorder
              onClick={() => {
                carouselApi?.scrollNext();
              }}
              disabled={!canScrollNext}
              className="disabled:pointer-events-auto"
            >
              <span className="material-symbols-outlined">
                arrow_forward
              </span>
            </Button>
          </div>
        </div>
      </div>
      <div className="relative w-full overflow-hidden mt-10 sm:mt-0">
        <Carousel
          setApi={setCarouselApi}
          opts={{
            align: "start",
            breakpoints: {
              "(max-width: 768px)": {
                dragFree: true,
              },
            },
          }}
          data-aos="fade-left"
        >
          <CarouselContent 
            className={cn(
              "mr-4 ml-0",
              "md:mr-8 md:ml-2",
              "2xl:mr-[max(0rem,calc(100vw/2-1336px/2))] 2xl:ml-[max(0rem,calc(100vw/2-1336px/2-24px))]"
            )}
          >
            {galleryItems?.map((item, index) => (
              <CarouselItem 
                key={index} 
                className="pl-4 md:pl-6 max-w-[328px] md:max-w-[436px]"
              >
                <div className="group flex flex-col justify-between">
                  <div className="flex aspect-[2/2] overflow-clip rounded-xl">
                    <div className="flex-1 ">
                      <div className="relative h-full w-full !transition-transform duration-300 ease-in-out group-hover:scale-105">
                        {item.image && (
                          <Image
                            src={item.image}  
                            alt={item.imageAltText ?? ""}
                            title={item.imageAltText ?? ""}
                            className="h-full w-full object-cover object-center "
                            fill
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <h5 className="line-clamp-2 break-words mt-10 mb-3">
                  {item.title}
                </h5>
                <div className="paragraph-base text-muted-foreground">
                  {parse(item.content ?? "")}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </SectionWrapper>
  );
};

export { Gallery1 };



